import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { Sentry } from './sentry.js'

// Static imports — entry points and lightweight pages
import Login from './pages/Login.jsx'
import Register from './pages/Register.jsx'
import ForgotPassword from './pages/ForgotPassword.jsx'
import ResetPassword from './pages/ResetPassword.jsx'
import VerifyEmail from './pages/VerifyEmail.jsx'
import ChildLogin from './pages/ChildLogin.jsx'
import ChildApp from './pages/ChildApp.jsx'
import Landing from './pages/Landing.jsx'
import Privacy from './pages/Privacy.jsx'
import FlyerPage from './pages/FlyerPage.jsx'
import { getStoredToken } from './auth.js'

// Lazy imports — loaded on demand
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'))
const Quests = lazy(() => import('./pages/Quests.jsx'))
const ActiveQuestPage = lazy(() => import('./pages/ActiveQuestPage.jsx'))
const Family = lazy(() => import('./pages/Family.jsx'))
const FamilySettings = lazy(() => import('./pages/FamilySettings.jsx'))
const CompletionHistory = lazy(() => import('./pages/CompletionHistory.jsx'))
const ChildProgress = lazy(() => import('./pages/ChildProgress.jsx'))
const Groups = lazy(() => import('./pages/Groups.jsx'))
const Feed = lazy(() => import('./pages/Feed.jsx'))
const Goals = lazy(() => import('./pages/Goals.jsx'))
const Join = lazy(() => import('./pages/Join.jsx'))
const FamilyInvite = lazy(() => import('./pages/FamilyInvite.jsx'))

// Prefetch map — call on nav hover/focus to load chunk before user clicks
export const prefetchRoute = (() => {
  const cache = new Set()
  const map = {
    '/dashboard': () => import('./pages/Dashboard.jsx'),
    '/quests':    () => import('./pages/Quests.jsx'),
    '/family':    () => import('./pages/Family.jsx'),
    '/groups':    () => import('./pages/Groups.jsx'),
    '/feed':      () => import('./pages/Feed.jsx'),
    '/goals':     () => import('./pages/Goals.jsx'),
  }
  return (path) => {
    if (cache.has(path) || !map[path]) return
    cache.add(path)
    map[path]().catch(() => {})
  }
})()

// Admin pages — heaviest, most benefit from lazy loading
const AdminQuests = lazy(() => import('./pages/AdminQuests.jsx'))
const AdminStats = lazy(() => import('./pages/AdminStats.jsx'))
const AdminAI = lazy(() => import('./pages/AdminAI.jsx'))
const AdminAIGuide = lazy(() => import('./pages/AdminAIGuide.jsx'))
const AdminBuild = lazy(() => import('./pages/AdminBuild.jsx'))
const AdminSettings = lazy(() => import('./pages/AdminSettings.jsx'))
const AdminFeedback = lazy(() => import('./pages/AdminFeedback.jsx'))
const AdminOperations = lazy(() => import('./pages/AdminOperations.jsx'))

function ChatRedirect() {
  const { id } = useParams()
  return <Navigate to="/groups" replace state={{ openChatGroupId: id }} />
}

function ProtectedRoute({ children, adminOnly = false }) {
  const token = getStoredToken()
  if (!token) return <Navigate to="/login" replace />
  if (adminOnly) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (!payload.isAdmin) return <Navigate to="/dashboard" replace />
    } catch { return <Navigate to="/login" replace /> }
  }
  return children
}



export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError, eventId }) => (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900 p-6">
        <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-800 p-8 shadow-lg text-center">
          <p className="text-5xl mb-4">😕</p>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">Något gick fel</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            Ett oväntat fel uppstod. Vi har loggat det automatiskt.
          </p>
          {eventId && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-4 font-mono">
              Fel-ID: {eventId}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={resetError}
              className="rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Försök igen
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="rounded-xl border border-slate-200 dark:border-slate-700 px-6 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Gå till startsidan
            </button>
          </div>
        </div>
      </div>
    )}>
      <Suspense fallback={null}>
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/child-login" element={<ChildLogin />} />
        <Route path="/child" element={<ChildApp />} />
        <Route path="/child/*" element={<ChildApp />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/quests" element={<ProtectedRoute><Quests /></ProtectedRoute>} />
        <Route path="/quests/active" element={<ProtectedRoute><ActiveQuestPage /></ProtectedRoute>} />
        <Route path="/family" element={<ProtectedRoute><Family /></ProtectedRoute>} />
        <Route path="/family/settings" element={<ProtectedRoute><FamilySettings /></ProtectedRoute>} />
        <Route path="/family/completions" element={<ProtectedRoute><CompletionHistory /></ProtectedRoute>} />
        <Route path="/family/children/:childId" element={<ProtectedRoute><ChildProgress /></ProtectedRoute>} />
        <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
        <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
        <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
        <Route path="/groups/:id/chat" element={<ProtectedRoute><ChatRedirect /></ProtectedRoute>} />
        <Route path="/admin/quests" element={<ProtectedRoute adminOnly><AdminQuests /></ProtectedRoute>} />
        <Route path="/admin/stats" element={<ProtectedRoute adminOnly><AdminStats /></ProtectedRoute>} />
        <Route path="/admin/ai" element={<ProtectedRoute adminOnly><AdminAI /></ProtectedRoute>} />
        <Route path="/admin/ai/guide" element={<ProtectedRoute adminOnly><AdminAIGuide /></ProtectedRoute>} />
        <Route path="/admin/operations" element={<ProtectedRoute adminOnly><AdminOperations /></ProtectedRoute>} />
        <Route path="/admin/build" element={<ProtectedRoute adminOnly><AdminBuild /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute adminOnly><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<ProtectedRoute adminOnly><AdminFeedback /></ProtectedRoute>} />
        <Route path="/join/:code" element={<Join />} />
        <Route path="/family-invite/:code" element={<FamilyInvite />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/flyer"   element={<FlyerPage />} />
        <Route path="/" element={<Landing />} />
      </Routes>
      </Suspense>
    </Sentry.ErrorBoundary>
  )
}
